"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play, X, Film } from "lucide-react";
import { Reveal } from "./Reveal";
import { SectionHeader } from "./SectionHeader";
import { IMG, MAISON } from "@/lib/diwline-data";

const SCENES = [
  { src: `${IMG}/hero.jpg`, legende: "Acte I — L'aube sur les champs dorés du Sénégal" },
  { src: `${IMG}/champs-arachide.jpg`, legende: "Acte II — La récolte digne des Reines Nourricières" },
  { src: `${IMG}/huilerie.jpg`, legende: "Acte III — Dans les cuves d'or de l'huilerie royale" },
  { src: `${IMG}/embouteillage.jpg`, legende: "Acte IV — L'embouteillage de luxe sous sceau royal" },
  { src: `${IMG}/fete-royale.jpg`, legende: "Acte V — De nos champs dorés aux tables du monde" },
];

const DUREE_SCENE = 3400;

function FilmDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [scene, setScene] = useState(0);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const t = setInterval(() => setScene(s => (s + 1) % SCENES.length), DUREE_SCENE);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      clearInterval(t);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[95] flex items-center justify-center bg-black/95 p-4 md:p-10"
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-label="Le Film Royal"
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 bg-royal/70 text-gold hover:bg-gold hover:text-royal"
            aria-label="Fermer le film"
          >
            <X className="h-5 w-5" />
          </button>

          <motion.div
            initial={{ scale: 0.95, y: 16 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.96 }}
            className="w-full max-w-4xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="relative overflow-hidden rounded-sm border-2 border-gold/50 shadow-[0_0_80px_rgba(212,175,55,0.25)]">
              <div className="relative aspect-video">
                <AnimatePresence mode="sync">
                  <motion.img
                    key={scene}
                    src={SCENES[scene].src}
                    alt={SCENES[scene].legende}
                    initial={{ opacity: 0, scale: 1.08 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ opacity: { duration: 1 }, scale: { duration: 3.6, ease: "linear" } }}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </AnimatePresence>
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/30" />
                <p className="absolute bottom-14 left-0 right-0 text-center font-elegant text-base md:text-xl italic text-cream px-6">
                  {SCENES[scene].legende}
                </p>
                <p className="absolute bottom-6 left-0 right-0 text-center text-[10px] uppercase tracking-[0.4em] text-gold">
                  DIW&apos;LINE présente · Le Film Royal
                </p>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-center gap-2" aria-hidden>
              {SCENES.map((_, i) => (
                <span
                  key={i}
                  className={`h-1 rounded-full transition-all duration-500 ${
                    i === scene ? "w-10 bg-gold" : "w-4 bg-cream/25"
                  }`}
                />
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function FilmRoyal() {
  const [open, setOpen] = useState(false);
  const [filmKey, setFilmKey] = useState(0);

  return (
    <section id="film" className="relative overflow-hidden bg-royal-brown py-24 md:py-32" aria-label="Le Film Royal">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          numero="IV"
          titre="Le Film Royal"
          intro={`Plongez dans l'univers de ${MAISON.souveraine} : des champs dorés du Sénégal aux tables du monde, suivez le voyage de l'or liquide africain.`}
        />

        <Reveal>
          <button
            type="button"
            onClick={() => {
              setFilmKey(k => k + 1);
              setOpen(true);
            }}
            className="group relative mx-auto block w-full max-w-4xl overflow-hidden rounded-sm border border-gold/40 focus-visible:outline-gold"
            aria-label="Visionner le film royal (2 minutes 45)"
          >
            <img
              src={`${IMG}/visite-royale.jpg`}
              alt="Sa Majesté visitant l'huilerie royale — affiche du film royal"
              className="aspect-video w-full object-cover transition-transform duration-[2s] group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/40" />

            <span className="absolute inset-0 flex items-center justify-center">
              <span className="relative inline-flex h-20 w-20 md:h-24 md:w-24 items-center justify-center rounded-full border-2 border-gold bg-black/45 backdrop-blur-sm transition-all duration-500 group-hover:scale-110 group-hover:bg-gold/20">
                <Play className="h-8 w-8 fill-gold text-gold transition-colors group-hover:fill-gold-light" aria-hidden />
                <span className="absolute inset-0 rounded-full border border-gold/60 animate-ping [animation-duration:2.6s]" aria-hidden />
              </span>
            </span>

            <span className="absolute bottom-5 left-6 right-6 flex flex-wrap items-center justify-between gap-3">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold-light">
                <Film className="h-4 w-4" aria-hidden />
                Le Film Royal
              </span>
              <span className="rounded-sm border border-gold/40 bg-black/50 px-3 py-1.5 text-xs font-semibold tracking-widest text-gold">
                2:45 · Prestige
              </span>
            </span>
          </button>
        </Reveal>
      </div>

      <FilmDialog key={filmKey} open={open} onClose={() => setOpen(false)} />
    </section>
  );
}
